import {
	Box,
	Button,
	ButtonGroup,
	Flex,
	IconButton,
	Input,
	InputGroup,
	InputProps,
	InputRightElement,
	Link,
	NumberDecrementStepper,
	NumberIncrementStepper,
	NumberInput,
	NumberInputField,
	NumberInputProps,
	NumberInputStepper,
	Select,
	SelectProps,
	Text,
	Textarea,
	TextareaProps,
	Tooltip
} from "@chakra-ui/react";
import {
	CreatableSelect as ChakraReactCreatableSelect,
	Select as ChakraReactSelect,
	MultiValue,
	OptionProps,
	SingleValueProps,
	components
} from "chakra-react-select";
import { isEmpty, omit } from "lodash";
import { ChangeEvent, FunctionComponent, ReactElement, ReactNode } from "react";
import { FaTrash } from "react-icons/fa";
import { ReactTags, Tag, TagSuggestion } from "react-tag-autocomplete";
import { emptyFunc } from "../../utils/consts";

const acceptedDecimalInput = new RegExp(/^[Ee0-9+\-.]$/);

// Constants

export type TextChangeEvent = ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>;

/**
 * @TODO: Do not add to or update this file. If you're here for debugging/development purposes,
 * make a new component instead and slowly deprecate this entire file.
 * See: `/DetailElement/Elements` for examples.
 */
interface DetailCustomFields {
	isEdit: boolean;
	isDeleteable?: boolean;
	isPreferred?: boolean; // Add a preferred marking
	type:
		| "string"
		| "number"
		| "decimal"
		| "datetime"
		| "date"
		| "select"
		| "chakra-select"
		| "multiselect"
		| "chakra-multiselect"
		| "button"
		| "email"
		| "website"
		| "boolean"
		| "textarea";
	displayValue: string | ReactNode | undefined;
	values?: { [id: string]: string };
	error?: string;
	unit?: string;
	showErrorText?: boolean;
	// Define custom change event handlers
	onTextChange?: (event: TextChangeEvent) => void;
	onNumberChange?: (value: number) => void;
	onDecimalChange?: (value: string) => void;
	onDelete?: () => void;

	// ReactTagsProps optional
	multiValues?: Array<TagSuggestion>;
	selected?: Array<Tag>;
	suggestions?: Array<TagSuggestion>;
	allowNew?: boolean;
	noOptionsText?: string;
	onMultiAdd?: (tag: Tag) => void;
	onMultiDelete?: (index: number) => void;

	// ChakraReactSelect optional
	/** Are the options grouped? */
	isGrouped?: boolean;
	options?: MultiValue<{
		label: string;
		value: string;
		icon?: ReactElement;
		options?: { label: string; value: string }[];
	}>;
	selectedOptions?: MultiValue<{ label: string; value: string; icon?: ReactElement }>;
	isLoading?: boolean;
	onMultiChange?: (tags: MultiValue<{ label: string; value: string }>) => void;
}

type DetailFieldProps = SelectProps &
	TextareaProps &
	// This adds far too many mandatory props for an optional type
	// Commented out to remind future devs that this exists
	// ReactTagsProps &
	NumberInputProps &
	InputProps &
	DetailCustomFields;

/**
 * Field that is text or an editable field
 */
const DetailField: FunctionComponent<DetailFieldProps> = (props: DetailFieldProps): ReactElement => {
	// eslint-disable-next-line @typescript-eslint/unbound-method
	const {
		isEdit,
		isDeleteable = false,
		type,
		unit,
		displayValue,
		values,
		multiValues,
		error,
		showErrorText = true,
		placeholder,
		isDisabled,
		isPreferred,
		allowNew,
		noOptionsText,
		selected,
		suggestions,
		isGrouped = false,
		options,
		selectedOptions,
		isLoading = false,
		onTextChange,
		onNumberChange,
		onDecimalChange,
		onMultiChange,
		onDelete,
		onMultiAdd,
		onMultiDelete
	} = props;

	// React generates a lot of warnings about invalid properties on fields
	// We need this to keep the console clear
	const inputProps = omit(props, ["isEdit", "displayValue", "onTextChange"]);

	const getSelectOption = (optionProps: OptionProps<{ label: string; value: string; icon?: ReactElement }>) => {
		const { Option } = components;
		return (
			<Option {...optionProps} key={`select-${optionProps.data.value}`}>
				{optionProps.data.icon && optionProps.data.icon}
				{optionProps.data.label}
			</Option>
		);
	};

	const getSingleValueOption = (
		valueProps: SingleValueProps<{ label: string; value: string; icon?: ReactElement }>
	) => {
		const { SingleValue: SingleValueComponent } = components;
		return (
			<SingleValueComponent {...valueProps}>
				{valueProps.data.icon && valueProps.data.icon} {valueProps.data.label}
			</SingleValueComponent>
		);
	};

	/**
	 * Helper function to determine whether the current field has an error
	 * @returns true or false
	 */
	const isError = (): boolean => !isEmpty(error);

	const getContent = (): ReactElement => {
		if (!isEdit) {
			switch (type.toLowerCase()) {
				default:
					return <Text wordBreak="break-word">{displayValue}</Text>;
				case "email":
					return (
						<Link
							wordBreak="break-word"
							color="orange.500"
							href={`mailto:${typeof displayValue == "string" ? displayValue ?? "" : ""}`}>
							{displayValue}
						</Link>
					);
				case "website":
					return (
						<Link
							wordBreak="break-word"
							color="orange.500"
							target="_blank"
							href={typeof displayValue == "string" ? displayValue ?? "" : ""}>
							{displayValue}
						</Link>
					);
			}
		}

		switch (type) {
			case "string":
			case "website":
			case "email":
				return (
					<Box display="flex">
						<Tooltip label={error} isDisabled={!showErrorText} textAlign="center">
							<InputGroup>
								<Input
									size="sm"
									mb="2"
									borderColor={isError() ? "red" : "gray.400"}
									_hover={{ borderColor: isError() ? "red" : "gray.300" }}
									borderRadius="3px"
									{...inputProps}
									maxWidth="400px"
									onChange={onTextChange}
								/>
								{unit && (
									<InputRightElement fontSize="sm" h="8">
										{unit}
									</InputRightElement>
								)}
							</InputGroup>
						</Tooltip>
						{isDeleteable && (
							<IconButton
								ml={2}
								size="sm"
								aria-label="Remove"
								background="transparent"
								icon={<FaTrash fontSize="15px" style={{ width: "18px", height: "18px" }} />}
								onClick={() => onDelete && typeof onDelete === "function" && onDelete()}
							/>
						)}
					</Box>
				);

			case "textarea":
				return (
					<Box display="flex">
						<Tooltip label={error} isDisabled={!showErrorText} textAlign="center">
							<InputGroup>
								<Textarea
									size="sm"
									mb="2"
									borderColor={isError() ? "red" : "gray.400"}
									_hover={{ borderColor: isError() ? "red" : "gray.300" }}
									borderRadius="3px"
									{...inputProps}
									maxWidth="400px"
									onChange={onTextChange}
								/>
							</InputGroup>
						</Tooltip>
						{isDeleteable && (
							<IconButton
								ml={2}
								size="sm"
								aria-label="Remove"
								background="transparent"
								icon={<FaTrash fontSize="15px" style={{ width: "18px", height: "18px" }} />}
								onClick={() => onDelete && typeof onDelete === "function" && onDelete()}
							/>
						)}
					</Box>
				);

			case "boolean":
				return (
					<ButtonGroup outline={"1px"} outlineColor={"gray.600"} isAttached>
						<Button
							size="xs"
							variant={props?.value === 1 ? "solid" : "outline"}
							colorScheme={props?.value === 1 ? "orange" : "blackAlpha"}
							_hover={{ backgroundColor: props?.value === 1 ? "orange.500" : "gray.100" }}
							minWidth="50px"
							borderRadius="4px"
							isDisabled={props.isDisabled}
							onClick={() => typeof onNumberChange === "function" && onNumberChange(1)}>
							Yes
						</Button>
						<Button
							size="xs"
							variant={props?.value === 0 ? "solid" : "outline"}
							colorScheme={props?.value === 0 ? "orange" : "blackAlpha"}
							_hover={{ backgroundColor: props?.value === 0 ? "orange.500" : "gray.100" }}
							minWidth="50px"
							borderLeft="0"
							ml={props?.value === 1 || props?.value === 0 ? "0" : "1px"}
							borderRadius="4px"
							isDisabled={props.isDisabled}
							onClick={() => typeof onNumberChange === "function" && onNumberChange(0)}>
							No
						</Button>
					</ButtonGroup>
				);

			case "number":
				return (
					<Flex maxW="150px">
						<Tooltip label={error} isDisabled={!showErrorText} textAlign="center">
							<NumberInput
								borderColor={isError() ? "red" : "gray.400"}
								_hover={{ borderColor: isError() ? "red" : "gray.300" }}
								size="sm"
								min={0}
								borderRadius="3px"
								{...props}
								onChange={(value, valueNumber) =>
									typeof onNumberChange === "function" && onNumberChange(valueNumber || 0)
								}
								isValidCharacter={(value) => acceptedDecimalInput.test(value)}>
								<NumberInputField fontSize={props.fontSize ?? "initial"} />
								<NumberInputStepper>
									<NumberIncrementStepper
										borderColor={props.variant === "flushed" ? "transparent" : isError() ? "red" : "gray.400"}
									/>
									<NumberDecrementStepper
										borderColor={props.variant === "flushed" ? "transparent" : isError() ? "red" : "gray.400"}
									/>
								</NumberInputStepper>
							</NumberInput>
						</Tooltip>
						{unit && (
							<Text as="span" fontSize="sm" ml="3" lineHeight="32px" color={props.isDisabled ? "gray.400" : "revert"}>
								{unit}
							</Text>
						)}
					</Flex>
				);

			case "decimal":
				return (
					<Flex maxW="150px" display={props.display ? props.display : "flex"}>
						<Tooltip label={error} isDisabled={!showErrorText} textAlign="center">
							<NumberInput
								borderColor={isError() ? "red" : "gray.400"}
								_hover={{ borderColor: isError() ? "red" : "gray.300" }}
								size="sm"
								min={0}
								borderRadius="3px"
								{...props}
								onChange={(value) => typeof onDecimalChange === "function" && onDecimalChange(value || "0")}
								isValidCharacter={(value) => acceptedDecimalInput.test(value)}>
								<NumberInputField fontSize={props.fontSize ?? "initial"} />
								<NumberInputStepper>
									<NumberIncrementStepper
										borderColor={props.variant === "flushed" ? "transparent" : isError() ? "red" : "gray.400"}
									/>
									<NumberDecrementStepper
										borderColor={props.variant === "flushed" ? "transparent" : isError() ? "red" : "gray.400"}
									/>
								</NumberInputStepper>
							</NumberInput>
						</Tooltip>
						{unit && (
							<Text as="span" fontSize="sm" ml="3" lineHeight="32px" color={props.isDisabled ? "gray.400" : "revert"}>
								{unit}
							</Text>
						)}
					</Flex>
				);

			// case "datetime":
			// 	// TODO: test and fix issues
			// 	return <Input {...props} type="datetime-local" onChange={onTextChange} />;

			// case "date":
			// 	return <Input {...props} type="date" onChange={onTextChange} max={new Date().toISOString().split("T")[0]} />;

			case "select":
				return (
					<Box marginTop="8px">
						<Tooltip label={error} isDisabled={!showErrorText} textAlign="center">
							<Select
								size="sm"
								borderColor={isError() ? "red" : "gray.400"}
								_hover={{ borderColor: isError() ? "red" : "gray.300" }}
								marginBottom="8px"
								borderRadius="3px"
								{...inputProps}
								onChange={onTextChange}>
								{values &&
									Object.keys(values).map((key) => {
										if (key === "") return <option selected hidden disabled value=""></option>;
										else
											return (
												<option key={key} value={key}>
													{values[key]}
												</option>
											);
									})}
							</Select>
						</Tooltip>
					</Box>
				);
			case "chakra-select":
				return (
					<Box marginBottom="8px">
						<Tooltip label={error} isDisabled={!showErrorText} textAlign="center">
							{allowNew ? (
								<ChakraReactCreatableSelect
									variant="flushed"
									options={options ?? []}
									placeholder={placeholder}
									value={selectedOptions ?? []}
									closeMenuOnSelect={true}
									isMulti={false}
									components={{ Option: getSelectOption, SingleValue: getSingleValueOption }}
									onChange={(option) =>
										onMultiChange && option
											? onMultiChange([{ label: option.label, value: option.value }])
											: emptyFunc()
									}
									isDisabled={isDisabled}
									isClearable={isDeleteable}
									hasStickyGroupHeaders={isGrouped}
									size="sm"
									menuPosition="fixed"
									menuShouldBlockScroll={true}
									maxMenuHeight={280}
									chakraStyles={{
										control: (provided, state) => ({
											...provided,
											opacity: state.isDisabled ? "0.8 !important" : 1,
											fontSize: "1rem"
										}),
										downChevron: (provided, state) => ({
											...provided,
											opacity: state.isDisabled ? "0 !important" : 1,
											fontSize: "20px"
										})
									}}
								/>
							) : (
								<ChakraReactSelect
									variant="flushed"
									options={options ?? []}
									placeholder={placeholder}
									value={selectedOptions ?? []}
									closeMenuOnSelect={true}
									isMulti={false}
									components={{ Option: getSelectOption, SingleValue: getSingleValueOption }}
									onChange={(option) =>
										onMultiChange
											? onMultiChange(option ? [{ label: option.label, value: option.value }] : [])
											: emptyFunc()
									}
									onInputChange={(event) =>
										onTextChange ? onTextChange({ target: { value: event } } as TextChangeEvent) : emptyFunc()
									}
									isDisabled={isDisabled}
									isClearable={isDeleteable}
									isLoading={isLoading}
									hasStickyGroupHeaders={isGrouped}
									size="sm"
									menuPosition="fixed"
									menuShouldBlockScroll={true}
									maxMenuHeight={280}
									chakraStyles={{
										control: (provided, state) => ({
											...provided,
											opacity: state.isDisabled ? "0.8 !important" : 1,
											fontSize: "1rem"
										}),
										downChevron: (provided, state) => ({
											...provided,
											opacity: state.isDisabled ? "0 !important" : 1,
											fontSize: "20px"
										})
									}}
								/>
							)}
						</Tooltip>
					</Box>
				);

			case "multiselect":
				return (
					<Box>
						<Tooltip label={error} isDisabled={!showErrorText} textAlign="center">
							<ReactTags
								labelText={placeholder}
								placeholderText={placeholder}
								selected={selected ?? []}
								suggestions={suggestions ?? []}
								onAdd={onMultiAdd ? onMultiAdd : emptyFunc}
								onDelete={onMultiDelete ? onMultiDelete : emptyFunc}
								isDisabled={isDisabled}
								allowNew={allowNew}
								noOptionsText={noOptionsText}
							/>
						</Tooltip>
					</Box>
				);
			case "chakra-multiselect":
				return (
					<Box marginBottom="8px">
						<Tooltip label={error} isDisabled={!showErrorText} textAlign="center">
							{allowNew ? (
								<ChakraReactCreatableSelect
									options={options ?? []}
									placeholder={placeholder}
									value={selectedOptions ?? []}
									closeMenuOnSelect={true}
									onChange={onMultiChange ? onMultiChange : emptyFunc}
									isMulti
									isDisabled={isDisabled}
									isClearable={isDeleteable}
									size="sm"
								/>
							) : (
								<ChakraReactSelect
									options={options ?? []}
									placeholder={placeholder}
									value={selectedOptions ?? []}
									closeMenuOnSelect={true}
									onChange={onMultiChange ? onMultiChange : emptyFunc}
									isMulti
									isDisabled={isDisabled}
									isClearable={isDeleteable}
									hasStickyGroupHeaders={isGrouped}
									size="sm"
								/>
							)}
						</Tooltip>
					</Box>
				);

			case "button":
				// TODO: test and fix issues
				return (
					<ButtonGroup size="sm" isAttached variant="outline">
						{values &&
							Object.keys(values).map((key) => {
								return (
									<Button key={key} isActive={props.value === key}>
										{values[key]}
									</Button>
								);
							})}
					</ButtonGroup>
				);

			default:
				return <></>;
		}
	};

	return (
		<>
			{getContent()}
			{isPreferred && " (Preferred)"}
		</>
	);
};

export default DetailField;
